import { useRouter } from 'next/router';
import { SVGProps, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import AiChatWidget from '@/quote/components/aiChat/AiChatWidget';
import LogoView from '@/quote/components/logo/GlobalLogoIcon';
import { useQuoteCloseWindow } from '@/quote/hooks/quoteInfo/useQuoteCloseWindow';
import { judgeWhetherUseMultilingual } from '@/quote/util/multilingual';
import { ChevronLeftIcon, InformationCircleIcon, XIcon } from '@heroicons/react/outline';
import { JoveLogoLoading } from '@jove-frontend/mui';
import {
  AmericanCountryList,
  Button,
  LocaleSwitch,
  Modal,
  NewOrganizationLogo,
  OrganizationLogo,
  PROVIDER_SOURCE,
  PoweredByJoveLogo,
  UKCountryList,
  ZendeskEmbed,
  apiGetOrganization,
  apiUpdateQuote,
  changeLanguage,
  getQuoteHeaderBrandingInfo,
  supportTranslationCountryConvertMap,
  supportTranslationCountryList,
  useLocalTranslation,
  useLocaleSwitch,
} from '@jove-frontend/uikit';
import clsx from 'clsx';
import { useStateMachine } from 'little-state-machine';
import qs from 'qs';
import { AppRoutesPaths } from '../types';
import { clearAction, updateAction } from '../util/update-action';
import InfoModal from './InfoModal';

interface HeaderProps {
  backRoute?: string;
  back?: () => void;
  hideBack?: boolean;
  hideClose?: boolean;
  localeChange?: (locale) => void;
  backDisabled?: boolean;
  alwaysShowLocaleSwitch?: boolean;
  hideLocaleSwitch?: boolean;
}

const CUSTOM_LOGO_ORG_ID_STORAGE_KEY = 'CUSTOM_LOGO_ORG_ID_STORAGE_KEY';

const Header = forwardRef((props: HeaderProps, ref) => {
  const { backRoute, back, hideBack, hideClose, localeChange, backDisabled, alwaysShowLocaleSwitch, hideLocaleSwitch } = props;
  const { $t } = useLocalTranslation();
  const router = useRouter();
  const { org_id, promotion_id } = router.query as {
    quote: string;
    theme: string;
    contract_id: string;
    org_id: string;
    promotion_id: string;
  };

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);
  const [isResumeModalOpen, setIsResumeModalOpen] = useState<boolean>(false);
  const [haveCustomLogo, setHaveCustomLogo] = useState(!!promotion_id);
  const [customLogo, setCustomLogo] = useState<string | null>(null);
  const [theme, setTheme] = useState<string>('default');
  const { currentLocaleCountry, modifyCurrentLocaleCountry } = useLocaleSwitch();
  const { state, actions, getState } = useStateMachine({
    clearAction,
    updateAction,
  });
  const [showLocaleSwitch, setShowLocalSwitch] = useState(false);
  const [showCustomLogo, setShowCustomLogo] = useState(false);
  const { closeQuoteAndPostParentMessage } = useQuoteCloseWindow();
  const [loading, setLoading] = useState(false);
  const [showAIChatView, setShowAIChatView] = useState(false);
  // Quote header branding configured on the CMS broker branding page, `logoUrl` already falls back to the company logo
  const quoteHeaderBrandingInfo = getQuoteHeaderBrandingInfo(state.promotionInfo);

  useEffect(() => {
    if (typeof window !== undefined) {
      const currentTheme = router.query.theme || router.query.channel || window.sessionStorage.getItem('jove-theme');

      if (theme) {
        setTheme(currentTheme as string);
      }
    }
  }, [router.query.theme, router.query.channel]);

  useEffect(() => {
    localeChange?.(currentLocaleCountry);
  }, [currentLocaleCountry]);

  useEffect(() => {
    const urlFlag = judgeWhetherUseMultilingual();
    // Not allowed countries
    const countryFlag = state?.contact?.country && ![...UKCountryList, ...AmericanCountryList].includes(state?.contact?.country);
    if (urlFlag && (alwaysShowLocaleSwitch || (!alwaysShowLocaleSwitch && countryFlag))) {
      setShowLocalSwitch(true);
    } else {
      setShowLocalSwitch(false);
    }

    // Custom logo
    if (quoteHeaderBrandingInfo.logoUrl) {
      setShowCustomLogo(true);
      setCustomLogo(quoteHeaderBrandingInfo.logoUrl);
    } else {
      setShowCustomLogo(router?.pathname === '/');
    }
  }, [router, state]);

  useEffect(() => {
    let showAIFlag = false;
    if (
      !state.promotionId &&
      !(
        !!state.providerCode &&
        [PROVIDER_SOURCE().GREATEAMERICAN_BUSINESS_INSURANCE.value, PROVIDER_SOURCE().HISCOX_US.value].includes(state.providerCode)
      )
    ) {
      showAIFlag = true;
    }
    setShowAIChatView(showAIFlag);
  }, [state.promotionId, state.providerCode]);

  useEffect(() => {
    if (org_id) {
      sessionStorage.setItem(CUSTOM_LOGO_ORG_ID_STORAGE_KEY, org_id);
    }
    const currentOrgId = state?.contact?.organization_id || org_id || sessionStorage.getItem(CUSTOM_LOGO_ORG_ID_STORAGE_KEY);
    const fetchValidation = async () => {
      const [organization, err] = await apiGetOrganization(currentOrgId);

      if (err) {
        console.log(err);
        return;
      }

      if (organization?.parent_organization && organization?.parent_organization?.logo) {
        setCustomLogo(organization.parent_organization.logo.url);
      } else if (organization && organization.logo) {
        setCustomLogo(organization.logo.url);
      }
    };

    if (currentOrgId) {
      fetchValidation();
    }
  }, [state?.contact?.organization_id]);

  useEffect(() => {
    const handleBack = () => {
      const realTimeState = getState();
      if (realTimeState?.meta?.isEditing) {
        actions.updateAction({
          ...realTimeState,
          meta: {
            ...realTimeState.meta,
            isEditing: false,
          },
        });
      }
    };

    window.addEventListener('popstate', handleBack);

    return () => {
      window.removeEventListener('popstate', handleBack);
    };
  }, []);

  const handleResumeQuote = () => {
    if (state.quoteId && state.meta?.route) {
      setIsModalOpen(false);

      router.push(state.meta?.route);
    }
  };

  const handleEndQuote = async () => {
    const payload = {
      meta: {
        ...state.meta,
        route: router.asPath,
      } as any,
    };

    setLoading(true);
    // save the last route the user was on
    if (state.quoteId) {
      await apiUpdateQuote(state.quoteId, payload);
    }
    setLoading(false);
    setIsModalOpen(false);

    const queryParams = {
      insuranceType: router.query.insuranceType || state.insuranceType,
    };
    router.push(`/end-quote?${qs.stringify(queryParams)}`);
    closeQuoteAndPostParentMessage();
  };

  const localeChangeAction = (item) => {
    modifyCurrentLocaleCountry(item);
    changeLanguage(supportTranslationCountryConvertMap[item]?.locale || supportTranslationCountryConvertMap.DEFAULT.locale);
  };

  const contactName = state?.contact?.first_name + state?.contact?.last_name || '';

  // Both fall back to the default header style when the broker has not configured them
  const headerContainerStyle = quoteHeaderBrandingInfo.backgroundColor ? { backgroundColor: quoteHeaderBrandingInfo.backgroundColor } : undefined;
  const customLogoContainerStyle = quoteHeaderBrandingInfo.logoHeight ? { height: quoteHeaderBrandingInfo.logoHeight } : undefined;

  const hideBackButton =
    router.pathname === AppRoutesPaths.Home ||
    router.pathname === AppRoutesPaths.C_Home ||
    router.pathname === AppRoutesPaths.Success ||
    router.pathname === AppRoutesPaths.C_Success ||
    (router.pathname.includes('success') && !router.pathname.includes('tripartite-completed')) ||
    router.pathname.includes('end-quote') ||
    hideBack;

  const hideCloseButton = router.pathname.includes('success') || router.pathname.includes('end-quote') || hideClose;

  if (!router.isReady) {
    return <div>{$t('kit:loading')}</div>;
  }

  const backAction = () => {
    if (backDisabled) {
      return;
    }

    if (backRoute) {
      router.push(backRoute);
    } else {
      if (back) {
        back();
      } else {
        router.back();
      }
    }
  };

  /**
   * External methods
   */
  useImperativeHandle(ref, () => ({ modifyProjectLanguage }));
  const modifyProjectLanguage = (info: { countryCode: string }) => {
    if (info?.countryCode) {
      localeChangeAction(info?.countryCode);
    }
  };

  return (
    <>
      <ZendeskEmbed />
      {/* Utility classes are `!important` in this workspace, so the configured values can only win via inline style when the conflicting class is removed */}
      <div
        className={clsx('sticky inset-x-0 top-0 z-[999] w-full flex-col items-center justify-between text-white', {
          'bg-header-bg': !quoteHeaderBrandingInfo.backgroundColor,
        })}
        style={headerContainerStyle}
      >
        {!hideLocaleSwitch && showLocaleSwitch && (
          <LocaleSwitch
            className="absolute right-10 top-0 z-[999] flex h-full !justify-center"
            supportCountries={supportTranslationCountryList}
            value={currentLocaleCountry}
            valueChange={localeChangeAction}
          />
        )}
        {/* {theme !== 'default' && (
          <div className="bg-brand-quirnary flex h-[32px] items-center justify-center text-xs text-black">
            <span>powered by</span>
            <NewJoveLogo className="h-[15px]" mainColor="#000" />
          </div>
        )} */}
        <div className="flex min-h-[64px] w-full items-center justify-between border-b border-neutral-300 py-3">
          <div className="w-10">
            {hideBackButton ? null : (
              <button
                onClick={backAction}
                className={`inline-flex min-w-[100px] items-center px-3 py-2 text-left text-sm font-semibold leading-4 ${
                  showCustomLogo && customLogo ? 'text-white' : 'text-header-color'
                } ${backDisabled ? 'cursor-not-allowed' : ''}`}
              >
                <ChevronLeftIcon className="mr-1 h-6 w-6" aria-hidden="true" />
                <span>{$t('back')}</span>
              </button>
            )}
          </div>
          <div className="flex flex-col items-center space-y-1">
            {showCustomLogo && customLogo ? (
              <div
                className={clsx('flex w-auto items-center space-x-2', { 'h-10': !quoteHeaderBrandingInfo.logoHeight })}
                style={customLogoContainerStyle}
              >
                {/* A configured height is honoured exactly, the default logo component caps the icon at 100px */}
                {quoteHeaderBrandingInfo.logoHeight ? (
                  <NewOrganizationLogo src={customLogo} className="h-full" />
                ) : (
                  <OrganizationLogo src={customLogo} size="small" />
                )}
              </div>
            ) : haveCustomLogo ? null : showAIChatView ? (
              <AiChatWidget defaultOpen={router.query['showAIChat'] === 'true'} />
            ) : (
              <LogoView />
            )}
          </div>
          {!hideCloseButton ? (
            <button
              title={$t('page:quoteHeader.close_quote')}
              aria-label={$t('page:quoteHeader.close_quote')}
              type="button"
              onClick={() => setIsModalOpen(true)}
              className={`inline-flex items-center px-3 py-2 text-left text-sm leading-4 ${
                showCustomLogo && customLogo ? 'text-white' : 'text-header-color'
              }`}
            >
              <XIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          ) : (
            <div className="w-10"></div>
          )}
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} variant="plain" title="" size="md">
        <div className="relative px-4 pb-5">
          {loading && <JoveLogoLoading />}
          <h3 className="text-brand-secondary mx-auto max-w-xs text-3xl font-extrabold">{$t('page:quoteHeader.end_quote_title')}</h3>
          <p className="py-6 text-center">
            {state.contact?.email
              ? $t('dynamicText:quoteHeader.end_quote_saved_with_email', { email: state.contact?.email })
              : $t('dynamicText:quoteHeader.end_quote_saved_without_email')}
            <InformationCircleIcon onClick={() => setIsInfoModalOpen(true)} className="ml-1 inline-block h-6" />
          </p>
          <div className="mt-4 flex w-full flex-col items-center justify-evenly space-y-3">
            <Button onClick={() => handleEndQuote()} className="w-full" loading={loading}>
              {$t('page:quoteHeader.end_quote_confirm')}
            </Button>

            <Button onClick={() => setIsModalOpen(false)} className="w-full" color="secondary-outline" disabled={loading}>
              {$t('page:quoteHeader.end_quote_cancel')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isResumeModalOpen} onClose={() => setIsResumeModalOpen(false)} variant="plain" size="md" title="">
        <div className="px-4 pb-5">
          <h3 className="text-brand-secondary mx-auto max-w-xs text-center text-3xl font-extrabold">
            {$t('page:quoteHeader.welcome_back', { name: contactName })}
          </h3>
          <p className="py-6 text-center">{$t('page:quoteHeader.resume_desc')}</p>

          <div className="mt-4 flex w-full flex-col items-center justify-evenly space-y-3">
            <Button onClick={() => handleResumeQuote()} className="w-full">
              {$t('page:quoteHeader.resume_quote')}
            </Button>
            <Button
              onClick={() => {
                setIsModalOpen(false);
                actions.clearAction();
                router.push(AppRoutesPaths.Home);
              }}
              className="w-full"
              color="secondary-outline"
            >
              {$t('page:quoteHeader.start_new_quote')}
            </Button>
          </div>
        </div>
      </Modal>
      <InfoModal title={$t('page:quoteHeader.information_title')} isOpen={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)}>
        <p>{$t('longStatement:quote_header_info_beta_desc')} </p>
        <p>
          {$t('longStatement:quote_header_info_insurance_desc')} <a href="mailto:customers@jove.co">customer@jove.co</a>
        </p>
      </InfoModal>
    </>
  );
});

export default Header;

const StudioSpaceLogo = () => (
  <div className="flex flex-col items-center space-y-1 md:flex-row">
    <StudioSpaceLogo />
    <PoweredByJoveLogo />
  </div>
);

const WamoLogo = (props: SVGProps<SVGSVGElement>) => (
  <div className="flex flex-col items-center space-y-1 md:flex-row">
    <WamoLogo />
    <PoweredByJoveLogo />
  </div>
);
